import { emailSchema, phoneSchema } from 'utils/schemas'
import { array, bool, mixed, number, object, string } from 'yup'

import SCHEMAS_MESSAGES from '../schemasMessages'
import { FORM_NAME_ERROR_MESSAGE } from './ConstantsFormBuilder'

function formatErrorMessage(path, message) {
  return `${path} ${message}`
}

const formBuilderSchema = object({
  formName: string().required(FORM_NAME_ERROR_MESSAGE),
  questions: array(
    object({
      num_columns: number().max(4, ({ path }) =>
        formatErrorMessage(path, 'Please add no more than 4 columns')
      ),
      image: mixed().when(
        [
          'question_type.question_type',
          'question_type.question_subtype',
          'loadedFromServer',
        ],
        (question_type, question_subtype, loadedFromServer, schema) => {
          if (
            question_type === 'file_upload' &&
            ['marker', 'handout'].includes(question_subtype) &&
            !loadedFromServer
          ) {
            return schema.required(({ path }) =>
              formatErrorMessage(
                path,
                `Please upload an ${
                  question_subtype === 'marker' ? 'image' : 'file'
                }`
              )
            )
          }
          return schema.nullable()
        }
      ),

      question_choices: array(
        object({
          text_value: string().required(({ path }) =>
            formatErrorMessage(
              path,
              SCHEMAS_MESSAGES.REQUIRED('texts for all choices')
            )
          ),
        })
      )
        .required()
        .when('question_type.question_type', (question_type, schema) => {
          if (['radio', 'checkbox'].includes(question_type)) {
            return schema.min(1, ({ path }) =>
              formatErrorMessage(
                path,
                SCHEMAS_MESSAGES.REQUIRED('at least one choice')
              )
            )
          }
          return schema
        }),

      question_columns: array(
        object({
          text_value: string().required(({ path }) =>
            formatErrorMessage(
              path,
              SCHEMAS_MESSAGES.REQUIRED('texts for all columns')
            )
          ),
        })
      )
        .required()
        .when(
          ['question_type.question_type', 'question_type.question_subtype'],
          (question_type, question_subtype, schema) => {
            if (
              question_type === 'matrix' ||
              (question_type === 'table' && question_subtype === 'open_ended')
            ) {
              return schema.min(1, ({ path }) =>
                formatErrorMessage(
                  path,
                  SCHEMAS_MESSAGES.REQUIRED('at least one column')
                )
              )
            }
            return schema
          }
        ),

      question_rows: array(
        object({
          text: string().when(
            ['question', '$getQuestion'],
            (question, getQuestion, schema) => {
              const _question = getQuestion(question)
              const { question_type } = _question

              if (question_type.question_type === 'matrix') {
                return schema.required(({ path }) =>
                  formatErrorMessage(
                    path,
                    SCHEMAS_MESSAGES.REQUIRED('texts for all rows')
                  )
                )
              }

              return schema.optional()
            }
          ),
        })
      )
        .required()
        .when(
          ['question_type.question_type', 'question_type.question_subtype'],
          (question_type, question_subtype, schema) => {
            if (
              question_type === 'matrix' ||
              (question_type === 'table' && question_subtype === 'open_ended')
            ) {
              return schema.min(1, ({ path }) =>
                formatErrorMessage(
                  path,
                  SCHEMAS_MESSAGES.REQUIRED('at least one row')
                )
              )
            }
            return schema
          }
        ),
      athena_integration: object({
        type: string(),
        field: string().required(({ path }) =>
          formatErrorMessage(path, 'Please complete athena configuration')
        ),
        label: string(),
        is_list: bool(),
      }).nullable(),
      chirotouch_integration: object({
        type: string(),
        field: string().required(({ path }) =>
          formatErrorMessage(path, 'Please complete chirotouch configuration')
        ),
        label: string(),
        is_list: bool(),
      }).nullable(),
      elation_integration: object({
        type: string(),
        field: string().required(({ path }) =>
          formatErrorMessage(path, 'Please complete elation configuration')
        ),
        label: string(),
        is_list: bool(),
      }).nullable(),
      open_dental_integration: object({
        type: string(),
        field: string().required(({ path }) =>
          formatErrorMessage(path, 'Please complete open dental configuration')
        ),
        label: string(),
        is_list: bool(),
      }).nullable(),
      file_extensions: array(string()).when(
        ['question_type.question_type'],
        (question_type, schema) => {
          if (question_type === 'file_upload') {
            return schema.min(1, ({ path }) =>
              formatErrorMessage(
                path,
                SCHEMAS_MESSAGES.REQUIRED('at least one file type')
              )
            )
          }
          return schema.nullable()
        }
      ),
      multi_file_upload: bool().when(
        ['question_type.question_type'],
        (question_type, schema) => {
          if (question_type === 'file_upload') {
            return schema.required(({ path }) =>
              formatErrorMessage(path, 'Please select an option')
            )
          }
          return schema.nullable()
        }
      ),
      default_value: mixed().when(
        ['question_type.question_type', 'question_type.question_subtype'],
        (question_type, question_subtype, schema) => {
          if (question_subtype === 'phone') {
            return schema
              .nullable()
              .transform((value) => (value === '' ? null : value))
              .test(
                'phone',
                'Default value is not a valid phone number',
                (value) => {
                  if (!value) {
                    return true
                  }
                  return phoneSchema.isValidSync(value)
                }
              )
          }
          if (question_subtype === 'email') {
            return schema
              .nullable()
              .transform((value) => (value === '' ? null : value))
              .test('email', 'Default value is not a valid email', (value) => {
                if (!value) {
                  return true
                }
                return emailSchema.isValidSync(value)
              })
          }
          if (question_type === 'datetime') {
            return schema.nullable()
          }
          return schema
            .nullable()
            .transform((value) => (value === '' ? null : value))
        }
      ),
    })
  ).min(1, SCHEMAS_MESSAGES.REQUIRED('at least one question')),
})

export default formBuilderSchema
